import React from 'react';
import { i18n } from 'config/locales';
import { emailTypeOptions } from 'constants/enums/emailOptions';
import { CheckboxModel, EmailModel, PvqEmailItemModel } from 'forms/shared/types';
import ValidationElement from '../ValidationElement';
import Generic from '../Generic';
import Field from '../Field';
import SelectDropdown from '../SelectDropdown';
import { setAttribute } from '../shared/setAttribute';
import Checkbox from '../Checkbox';
import { ValidationElementProps } from '../ValidationElement/ValidationElement';
import SvgComponent from '../SvgComponent';
import LinkButton from '../LinkButton';

interface Props extends ValidationElementProps, Partial<EmailModel> {
showAnotherEmail: () => void,
   removeThisEmail: () => void,
  showAccordianActions?: boolean,
  preventRepeatQuestions?: boolean,
}

export default class Email extends ValidationElement<Props> {
  constructor(props) {
    super(props);

    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
    };

    this.handleError = this.handleError.bind(this);
    this.emailTypeRef = React.createRef();
  }

  /**
   * Handle the change event.
   */
  handleChange = (event) => {
    const {
      onUpdate, name, emailType, emailUnknown
    } = this.props;
    const { value } = event.target;

    super.handleChange(event);

    if (onUpdate) {
      onUpdate({
        name, value, emailType, emailUnknown
      });
    }
  };

  update = (queue: Partial<EmailModel>) => {
    const {
      name, value, emailType, emailUnknown
    } = this.props;
    this.props.onUpdate({
      name,
      value,
      emailType,
      emailUnknown,
      ...queue,
    });
  };

  showAnotherEmail = () => {
    this.props?.showAnotherEmail?.();
  };

  removeThisEmail = () => {
    this.props?.removeThisEmail?.();
  };

  dontKnowThisEmail = () => {
    this.props?.dontKnowThisEmail?.();
  };

  updateEmailUnknown = (cbx: Partial<CheckboxModel>) => {
    this.update({
      emailUnknown: cbx?.checked,
      value: cbx?.checked ? '' : this.props.value,
      emailType: cbx?.checked ? '' : this.props.emailType,
    });
  };

  handleError(value, arr) {
    const arr2 = arr.map((err) => ({
      code: `email.${err.code}`,
      valid: err.valid,
      uid: err.uid,
    }));

    return this.props.onError(value, arr2);
  }

  handleErrorEmailType = (value, arr) => {
    const arr2 = arr.map((err) => ({
      code: `email.emailType.${err.code}`,
      valid: err.valid,
      uid: err.uid,
    }));

    return this.props.onError(value, arr2);
  };

  render() {
    const {
      pattern, value, emailType, includeEmailType, prefix, emailFieldTitle,
    } = this.props;

    let emailTitle = emailFieldTitle || i18n.t('identification.pvq.contacts.heading.emailAddress');

    if (this.props.preventRepeatQuestions) {
      emailTitle = '';
    }

    return (
      <>
        <Field
          optional
          title={emailTitle}
          scrollIntoView={this.props.scrollIntoView}
          attribute={setAttribute(this.props.attribute, 'value')}
          className="minimal-margin-bottom"
          exactAttribute
        >
          <Generic
            attribute={this.props.attribute}
            name={this.props.name}
            label={this.props.label}
            ariaLabel={this.props.ariaLabel}
            placeholder={this.props.placeholder}
            className={`email ${this.props.className || ''}`.trim()}
            type="text"
            disabled={this.props.disabled || this.props.emailUnknown}
            maxlength={this.props.maxlength}
            pattern={pattern}
            readonly={this.props.readonly}
            required={this.props.required && !this.props.emailUnknown}
            autocapitalize={this.props.autocapitalize}
            autocorrect={this.props.autocorrect}
            autocomplete={this.props.autocomplete}
            spellcheck={this.props.spellcheck}
            value={value}
            onFocus={this.props.Focus}
            onBlur={this.handleChange}
            onError={this.handleError}
          />
        </Field>
        {this.props.showEmailUnknown
          && (
          <>
            <div className="or">{i18n.t('email.label.or')}</div>
            <Checkbox
              name="emailUnknown"
              className={this.props.showEmailUnknown ? 'emailUnknown' : 'hidden'}
              label={i18n.t('email.label.emailUnknown')}
              checked={this.props.emailUnknown}
              onUpdate={this.updateEmailUnknown}
              onError={this.handleErrorEmailType}
            />
          </>
          )}
        {includeEmailType && (
        <Field
          title={i18n.t(`${prefix}.label.emailType`)}
          titleSize="label"
          help={`${prefix}.help.emailType`}
          errorPrefix="name"
          className="usa-form-control"
          filterErrors={this.filterErrors}
          scrollIntoView={this.props.scrollIntoView}
          optional
          labelFor={`selectDropdown-${this.state.uid}`}
          attribute={setAttribute(this.props.attribute, 'emailType')}
        >
          <SelectDropdown
            className="option-list usa-medium-input"
            isDisabled={this.props.disabled || this.props.emailUnknown?.checked === true}
            ref={this.emailTypeRef}
            name="emailType"
            ariaLabel="Email Type"
            onChange={(e) => {
              this.update({
                emailType: e.target.value,
              });
            }}
            onError={this.handleErrorEmailType}
            value={emailType || ''}
            required={false}
            labelKey={`selectDropdown-${this.state.uid}`}
            attribute={setAttribute(this.props.attribute, 'emailType')}
            id={`selectDropdown-${this.state.uid}`}
          >
            {emailTypeOptions.map((x, index) => (
              <option key={`${x.key}-${index}`} value={`${x.value ?? ''}`}>
                {`${x.text ?? ''}`}
              </option>
            ))}
          </SelectDropdown>
        </Field>
        )}
        {this.props.showAccordianActions && (
          <>
            {(!this.props?.showRemoveItem && !this.props.showEmailUnknown && this.props?.$index === 0)
            && ( // must always show once but not if already included, when accordian/branch actions exist
            <Checkbox
              name="emailUnknown"
              className="emailUnknown"
              label={i18n.t('email.label.emailUnknown')}
              checked={this.props.emailUnknown}
              onUpdate={this.updateEmailUnknown}
              onError={this.handleErrorEmailType}
            />
            )}
            {(!this.props.emailUnknown && !!this.props.value) && (
            <LinkButton
              classContent="email-button add"
              ignorePaddingMargin
              onClick={this.showAnotherEmail}
            >
              <span>Add More</span>
            </LinkButton>
            )}
            {this.props?.showRemoveItem && (
            <LinkButton
              classContent="email-button remove"
              ignorePaddingMargin
              onClick={this.removeThisEmail}
            >
              <span>Remove</span>
            </LinkButton>

            )}
          </>
        )}
      </>
    );
  }
}

Email.defaultProps = {
  pattern: /^([A-z0-9_.-]+)@([A-z0-9.-]+)\.+([A-z.]{2,63})$/,
  value: '',
  emailType: '',
  spellcheck: false,
  autocapitalize: false,
  autocorrect: false,
  autocomplete: false,
  includeEmailType: false,
  showEmailUnknown: false,
  emailUnknown: false,
  emailFieldTitle: '',
  showAccordianActions: false,
  showRemoveItem: false,
  preventRepeatQuestions: false,
  prefix: 'identification.pvq.contacts',
  onError: (value, arr) => arr,
  showAnotherEmail: () => {},
  removeThisEmail: () => {},
  dontKnowThisEmail: () => {},
};

Email.errors = [];
